/**
 * 文件传输协议 v1 —— 共享协议层(纯函数,无副作用)。
 *
 * 严格对齐 `docs/reference/protocol/file-transfer.md`,是 Console 侧 wire 契约的
 * 单一实现来源:envelope 编解码、§6.2 二进制帧 framing、chunkSize 协商、§4.1 glare
 * tie-break、文件名 sanitize、SHA-256。macOS(`Signal/FileMessageCoding.swift`)/
 * Windows 须产生逐字节一致的 wire 行为——见 `protocol.test.ts` 的共享测试向量。
 *
 * 本模块不碰 DOM / data channel / store,便于纯单测;通道接入与收发状态机在
 * `session.ts`(`transfer.ts` 是 `sendFile` 薄封装)。
 */

// ── 常量(§6.1 / §6.2 / §7.1 / §8 / §11)─────────────────────────────

/** 文件传输协议版本;与 remote-input 的 v:2 相互独立(file-transfer.md 文首)。 */
export const FILE_PROTOCOL_VERSION = 1 as const;

/** 单条 binary payload 上限 64 KiB(§6.2 / §11)。 */
export const MAX_PAYLOAD_BYTES = 64 * 1024;
/** chunk header(JSON)上限 4 KiB(§6.2 / §11)。 */
export const MAX_CHUNK_HEADER_BYTES = 4 * 1024;
/** 单条 JSON 控制消息上限 64 KiB(§6.1 / §11)。 */
export const MAX_CONTROL_JSON_BYTES = 64 * 1024;
/** headerBudget:framing 头预留(§6.2),v1 固定 4 KiB。 */
export const HEADER_BUDGET_BYTES = 4 * 1024;
/** 启用 v1 所需的最小 maxMessageSize = 4 KiB header + 64 KiB payload(§6.2 / §7.1)。 */
export const MIN_MAX_MESSAGE_SIZE = HEADER_BUDGET_BYTES + MAX_PAYLOAD_BYTES; // 69632
/** 背压高水位:bufferedAmount 超过则暂停读取(§8.9)。 */
export const BACKPRESSURE_HIGH_BYTES = 4 * 1024 * 1024;
/** 背压低水位:bufferedAmount 低于则续读(§8.9)。 */
export const BACKPRESSURE_LOW_BYTES = 1 * 1024 * 1024;
/** 收到对端 file:hello 的等待超时;超时按 unsupported 处理(§7.1)。 */
export const HELLO_TIMEOUT_MS = 5000;
/** data channel wire label(§4;代码字段名 dc_file 仅为本地命名)。 */
export const FILE_CHANNEL_LABEL = "file";
/** file:hello 固定 transfer id(§6.1 / §7.1,不参与唯一性)。 */
export const HELLO_ID = "session";

// ── 类型(§3 / §7)──────────────────────────────────────────────────

export type FileEndpointRole = "controller" | "host";
export type FileCapability = "send" | "receive";

/** file:reject.reason 闭集(§5 / §7.4)。 */
export type FileRejectReason =
	| "paid_required"
	| "disabled"
	| "busy"
	| "too_large"
	| "user_rejected"
	| "superseded";

/** file:cancel.reason 闭集(§7.7)。 */
export type FileCancelReason =
	| "user_cancelled"
	| "session_closed"
	| "superseded";

/** file:error.code 闭集(§7.8)。 */
export type FileErrorCode =
	| "protocol_error"
	| "io_error"
	| "checksum_mismatch"
	| "channel_closed"
	| "quota_exceeded";

/** 控制 envelope 公共字段(§6.1)。 */
interface FileEnvelopeBase {
	v: typeof FILE_PROTOCOL_VERSION;
	type: string;
	id: string;
	/** sender timestamp, ms since epoch(可选)。 */
	t?: number;
}

export interface FileHello extends FileEnvelopeBase {
	type: "file:hello";
	id: typeof HELLO_ID;
	endpointRole: FileEndpointRole;
	capabilities: FileCapability[];
	maxFileSize: number;
	maxMessageSize: number;
}

export interface FileOffer extends FileEnvelopeBase {
	type: "file:offer";
	name: string;
	size: number;
	mime?: string;
	sha256: string;
}

export interface FileAccept extends FileEnvelopeBase {
	type: "file:accept";
	chunkSize: number;
}

export interface FileReject extends FileEnvelopeBase {
	type: "file:reject";
	reason: FileRejectReason;
	message?: string;
}

export interface FileProgress extends FileEnvelopeBase {
	type: "file:progress";
	receivedBytes: number;
	totalBytes: number;
}

export interface FileComplete extends FileEnvelopeBase {
	type: "file:complete";
	receivedBytes: number;
	sha256: string;
	savedAs?: string;
}

export interface FileCancel extends FileEnvelopeBase {
	type: "file:cancel";
	reason: FileCancelReason;
}

export interface FileError extends FileEnvelopeBase {
	type: "file:error";
	code: FileErrorCode;
	message?: string;
}

/** §6.2 二进制帧的 JSON 头。 */
export interface FileChunkHeader {
	v: typeof FILE_PROTOCOL_VERSION;
	type: "file:chunk";
	id: string;
	/** 从 0 起递增。 */
	seq: number;
	/** payload 在原文件中的起始字节偏移。 */
	offset: number;
	/** payload 字节数。 */
	size: number;
}

export type FileControlMessage =
	| FileHello
	| FileOffer
	| FileAccept
	| FileReject
	| FileProgress
	| FileComplete
	| FileCancel
	| FileError;

// ── 控制消息编解码(§6.1)────────────────────────────────────────────

const KNOWN_CONTROL_TYPES = new Set([
	"file:hello",
	"file:offer",
	"file:accept",
	"file:reject",
	"file:progress",
	"file:complete",
	"file:cancel",
	"file:error",
]);

const isKnownControlType = (type: string): boolean =>
	KNOWN_CONTROL_TYPES.has(type);

/**
 * 解析 file channel 文本帧为强类型控制消息。仿 `negotiation.ts` 的容错范式:
 * 非 JSON / 非对象 / `v !== 1` / 未知 type → 返回 null 并 warn(调用方忽略)。
 */
export const parseFileControl = (data: unknown): FileControlMessage | null => {
	if (typeof data !== "string") {
		// 文本帧才走控制消息;二进制帧应交给 decodeChunkFrame。
		return null;
	}
	// §6.1 是双向 wire 限制:接收端也必须在 parse 前拒绝超大控制帧,
	// 防止恶意/错误对端用超大 JSON 拖垮浏览器解析。
	const byteLen = utf8ByteLength(data);
	if (byteLen > MAX_CONTROL_JSON_BYTES) {
		console.warn("[file-transfer] discarded oversized control frame:", byteLen);
		return null;
	}
	let raw: unknown;
	try {
		raw = JSON.parse(data);
	} catch {
		console.warn("[file-transfer] discarded non-JSON control frame");
		return null;
	}
	if (typeof raw !== "object" || raw === null) return null;
	const msg = raw as Record<string, unknown>;
	if (msg.v !== FILE_PROTOCOL_VERSION) {
		console.warn(
			"[file-transfer] discarded non-v1 control message:",
			msg.type,
			"v=",
			msg.v,
		);
		return null;
	}
	if (typeof msg.type !== "string" || typeof msg.id !== "string") return null;
	if (!isKnownControlType(msg.type)) {
		console.warn("[file-transfer] ignored unknown control type:", msg.type);
		return null;
	}
	// 形态校验交给各 handler;此处只保证 v/type/id 合法。
	return msg as unknown as FileControlMessage;
};

/** 序列化控制消息为文本帧(自动补 v)。超 §6.1 上限抛错。 */
export const encodeFileControl = <T extends Omit<FileControlMessage, "v">>(
	msg: T,
): string => {
	const json = JSON.stringify({ v: FILE_PROTOCOL_VERSION, ...msg });
	if (utf8ByteLength(json) > MAX_CONTROL_JSON_BYTES) {
		throw new Error(
			`[file-transfer] control message exceeds ${MAX_CONTROL_JSON_BYTES} bytes`,
		);
	}
	return json;
};

// ── 二进制帧 framing(§6.2)──────────────────────────────────────────
//
// 线格式:[uint32be headerLength][header JSON UTF-8][payload]
// 一条 WebRTC binary message 必须正好承载一个完整 chunk frame。

/**
 * 编码一个二进制 chunk 帧。`header.size` 必须等于 payload 字节数。
 * 越界(headerLength > 4 KiB 或 payload > 64 KiB)抛错。
 */
export const encodeChunkFrame = (
	header: FileChunkHeader,
	payload: ArrayBuffer | Uint8Array,
): ArrayBuffer => {
	const payloadBytes =
		payload instanceof Uint8Array ? payload : new Uint8Array(payload);
	if (payloadBytes.byteLength < 1) {
		throw new Error(
			"[file-transfer] empty chunk payload not allowed (size must be >= 1)",
		);
	}
	if (payloadBytes.byteLength > MAX_PAYLOAD_BYTES) {
		throw new Error(
			`[file-transfer] payload ${payloadBytes.byteLength} exceeds ${MAX_PAYLOAD_BYTES}`,
		);
	}
	if (header.size !== payloadBytes.byteLength) {
		throw new Error(
			"[file-transfer] chunk header.size mismatch payload length",
		);
	}
	const headerBytes = new TextEncoder().encode(JSON.stringify(header));
	if (headerBytes.byteLength > MAX_CHUNK_HEADER_BYTES) {
		throw new Error(
			`[file-transfer] chunk header ${headerBytes.byteLength} exceeds ${MAX_CHUNK_HEADER_BYTES}`,
		);
	}
	const out = new Uint8Array(
		4 + headerBytes.byteLength + payloadBytes.byteLength,
	);
	new DataView(out.buffer).setUint32(0, headerBytes.byteLength, false); // big-endian
	out.set(headerBytes, 4);
	out.set(payloadBytes, 4 + headerBytes.byteLength);
	return out.buffer;
};

export interface DecodedChunk {
	header: FileChunkHeader;
	payload: Uint8Array;
}

/**
 * 解析一个二进制 chunk 帧。任何结构 / 越界 / `v!==1` / `type!=="file:chunk"` /
 * size 与 payload 不符 → 返回 null(调用方按协议错误取消)。
 */
export const decodeChunkFrame = (
	buf: ArrayBuffer | Uint8Array,
): DecodedChunk | null => {
	const bytes = buf instanceof Uint8Array ? buf : new Uint8Array(buf);
	if (bytes.byteLength < 4) return null;
	const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
	const headerLength = view.getUint32(0, false);
	if (headerLength === 0 || headerLength > MAX_CHUNK_HEADER_BYTES) return null;
	if (4 + headerLength > bytes.byteLength) return null;
	let header: unknown;
	try {
		const headerJson = new TextDecoder().decode(
			bytes.subarray(4, 4 + headerLength),
		);
		header = JSON.parse(headerJson);
	} catch {
		return null;
	}
	if (!isValidChunkHeader(header)) return null;
	const payload = bytes.subarray(4 + headerLength);
	if (header.size !== payload.byteLength) return null;
	if (payload.byteLength > MAX_PAYLOAD_BYTES) return null;
	// 复制出独立 buffer,避免持有底层大 buffer 视图。
	return { header, payload: payload.slice() };
};

const isValidChunkHeader = (h: unknown): h is FileChunkHeader => {
	if (typeof h !== "object" || h === null) return false;
	const r = h as Record<string, unknown>;
	return (
		r.v === FILE_PROTOCOL_VERSION &&
		r.type === "file:chunk" &&
		typeof r.id === "string" &&
		isNonNegativeInt(r.seq) &&
		isNonNegativeInt(r.offset) &&
		// §7.3:有效 chunk size ∈ [1, 65536];0 字节 chunk 无合法用途,拒绝。
		isPositiveInt(r.size, MAX_PAYLOAD_BYTES)
	);
};

// ── chunkSize 协商(§6.2 / §7.3)─────────────────────────────────────

/** §7.3:accept.chunkSize 必须是 [1, 65536] 内整数。 */
export const isValidAcceptChunkSize = (n: unknown): n is number =>
	typeof n === "number" &&
	Number.isInteger(n) &&
	n >= 1 &&
	n <= MAX_PAYLOAD_BYTES;

export interface ChunkSizeInputs {
	/** 本端 sender 偏好(本地配置,不上 wire)。 */
	localPreferred: number;
	/** receiver 在 file:accept 给出的 chunkSize(已校验合法)。 */
	receiverAccept: number;
	/** 本端 maxMessageSize(来自本端 hello)。 */
	localMaxMessageSize: number;
	/** 对端 maxMessageSize(来自对端 hello)。 */
	remoteMaxMessageSize: number;
}

/**
 * §6.2:有效 chunk size =
 * min(localPreferred, receiverAccept, localMaxPayload, remoteMaxPayload, 64 KiB)。
 * localMaxPayload/remoteMaxPayload = maxMessageSize - headerBudget。
 * 调用方须先用 isValidAcceptChunkSize / maxMessageSizeSupportsV1 校验入参。
 */
export const negotiateChunkSize = (i: ChunkSizeInputs): number => {
	// 不“正常化”非法入参:receiverAccept 非法时(§7.3)调用方须据此发
	// file:error protocol_error,这里 throw 兜底,杜绝把 0 / NaN / 越界值带进发送循环。
	if (!isValidAcceptChunkSize(i.receiverAccept)) {
		throw new Error(
			`[file-transfer] invalid accept.chunkSize: ${String(i.receiverAccept)}`,
		);
	}
	if (
		!maxMessageSizeSupportsV1(i.localMaxMessageSize) ||
		!maxMessageSizeSupportsV1(i.remoteMaxMessageSize)
	) {
		throw new Error("[file-transfer] maxMessageSize below v1 minimum (§6.2)");
	}
	if (!Number.isInteger(i.localPreferred) || i.localPreferred < 1) {
		throw new Error(
			`[file-transfer] invalid localPreferred: ${String(i.localPreferred)}`,
		);
	}
	const localMaxPayload = i.localMaxMessageSize - HEADER_BUDGET_BYTES;
	const remoteMaxPayload = i.remoteMaxMessageSize - HEADER_BUDGET_BYTES;
	// 入参均已校验为 >= 1 的整数(payload 余量恒 >= 64 KiB),min 结果必为 [1, 65536] 整数。
	return Math.min(
		i.localPreferred,
		i.receiverAccept,
		localMaxPayload,
		remoteMaxPayload,
		MAX_PAYLOAD_BYTES,
	);
};

/** §6.2:maxMessageSize < 68 KiB 必须拒绝文件传输并降级。 */
export const maxMessageSizeSupportsV1 = (maxMessageSize: number): boolean =>
	typeof maxMessageSize === "number" && maxMessageSize >= MIN_MAX_MESSAGE_SIZE;

// ── glare tie-break(§4.1)────────────────────────────────────────────

export type GlareOutcome = "local-wins" | "incoming-wins" | "collision";

/**
 * §4.1:两端几乎同时发 offer 时,按 transfer id ASCII 字典序收敛。
 * id 更小者胜出;相同 → collision(违反 §6.1 唯一性的兜底,双方发 file:error)。
 */
export const resolveGlare = (
	localId: string,
	incomingId: string,
): GlareOutcome => {
	if (localId === incomingId) return "collision";
	return localId < incomingId ? "local-wins" : "incoming-wins";
};

/** 生成 transfer id(随机 UUID v4)。 */
export const newTransferId = (): string => crypto.randomUUID();

// ── 文件名 sanitize(§10)────────────────────────────────────────────

const MAX_FILENAME_LENGTH = 180;
// 路径分隔 / 卷标 / Windows 保留符
const FILENAME_RESERVED = /[/\\:*?"<>|]/g;
// 控制字符:0x00–0x1f 与 0x7f
// biome-ignore lint/suspicious/noControlCharactersInRegex: 故意剥离控制字符(§10 安全要求)
const FILENAME_CONTROL = /[\x00-\x1f\x7f]/g;
const FILENAME_LEADING_DOTS = /^\.+/;

/**
 * §10:去路径分隔/卷标语义(/ \ :)、控制字符,限长 180,空名→untitled。
 * 这是 receiver 写盘前的最后防线,确保不能逃出接收目录(验收 #10)。
 */
export const sanitizeFileName = (name: string): string => {
	let out = (typeof name === "string" ? name : "")
		.replace(FILENAME_RESERVED, "")
		.replace(FILENAME_CONTROL, "")
		.trim()
		// 去掉首部点,避免隐藏文件 / 纯点名(. 与 ..)
		.replace(FILENAME_LEADING_DOTS, "");
	if (out.length > MAX_FILENAME_LENGTH) out = out.slice(0, MAX_FILENAME_LENGTH);
	out = out.trim();
	return out.length > 0 ? out : "untitled";
};

// ── SHA-256(§7.2 / §7.6)────────────────────────────────────────────

/**
 * 计算 SHA-256 十六进制小写串。crypto.subtle 仅在安全上下文(https/localhost)可用——
 * dashboard 部署需 https(见 file-transfer.md 风险记录)。
 */
export const sha256Hex = async (data: BufferSource): Promise<string> => {
	// BufferSource(ArrayBuffer 或 ArrayBufferView)直接传入;view 会按其
	// byteOffset/byteLength 区间哈希,无需 slice。
	const digest = await crypto.subtle.digest("SHA-256", data);
	return [...new Uint8Array(digest)]
		.map((b) => b.toString(16).padStart(2, "0"))
		.join("");
};

// ── 小工具 ───────────────────────────────────────────────────────────

const isNonNegativeInt = (n: unknown): n is number =>
	typeof n === "number" && Number.isInteger(n) && n >= 0;

const isPositiveInt = (n: unknown, max: number): n is number =>
	typeof n === "number" && Number.isInteger(n) && n >= 1 && n <= max;

const utf8ByteLength = (s: string): number =>
	new TextEncoder().encode(s).byteLength;
